import { useMemo } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { WorkStatus, CatJobHistoryItem } from "../../../client/types.gen";
import { formatExactMoneyDollars } from "../Money";
import { formatDuration } from "../icm/util";
import { formatTimestamp } from "../utils";
import { SelectedModel } from "./types";
import styles from "./CatCard.module.css";

// Per-row recovery for the pipeline models (RMS / RMS 22 / ICM): resume a
// failed run from where it stalled, or re-run only the financial stage of
// a completed one. Each acts on that row's own batch. Null for models that
// don't ride a pipeline. ``onRestart``: like resume, but failed chunks discard
// their potentially stuck request and submit fresh. ``actionInFlightBatchId``
// is the batch whose recovery is currently being kicked off (for the spinner + to disable the
// rest); ``disabled`` covers a run already in progress for this model.
interface EltRecoveryControls {
  onResume: (batchId: string) => void;
  onRestart?: (batchId: string) => void;
  onRerunFinancials: (batchId: string) => void;
  actionInFlightBatchId: string | null;
  disabled: boolean;
}

interface RunHistoryTabProps {
  jobHistory: CatJobHistoryItem[];
  selectedModel: SelectedModel;
  viewingBatchId: string | null;
  onViewJobResults: (job: CatJobHistoryItem) => void;
  onDownloadAll: (job: CatJobHistoryItem) => void;
  eltRecovery?: EltRecoveryControls | null;
}

const renderStatusCell = (job: CatJobHistoryItem) => {
  const modelTag = (
    <Tag
      value={
        job.type === "spatialkat"
          ? "KatRisk"
          : job.type === "rms_elt"
            ? "RMS"
            : job.type === "rms_22"
              ? "RMS 22"
              : job.type === "icm_ylt"
                ? "ICM"
                : "ICM (Legacy)"
      }
      severity={
        job.type === "icm"
          ? "info"
          : job.type === "spatialkat"
            ? "success"
            : job.type === "rms_elt"
              ? "danger"
              : job.type === "rms_22"
                ? "danger"
                : "warning"
      }
      className={styles.modelTag}
    />
  );

  const isRunning =
    job.status === WorkStatus.PENDING || job.status === WorkStatus.IN_PROGRESS;

  if (isRunning) {
    return (
      <div className={styles.statusRunning}>
        <ProgressSpinner className={styles.spinnerSmall} strokeWidth="4" />
        <span>In Progress</span>
        {modelTag}
      </div>
    );
  }

  switch (job.status) {
    case WorkStatus.DONE:
      return (
        <span className={styles.statusCompleted}>Completed {modelTag}</span>
      );
    case WorkStatus.FAILED:
      return <span className={styles.statusFailed}>Failed {modelTag}</span>;
    default:
      return (
        <span>
          {job.status} {modelTag}
        </span>
      );
  }
};

export const RunHistoryTab: React.FC<RunHistoryTabProps> = ({
  jobHistory,
  selectedModel,
  viewingBatchId,
  onViewJobResults,
  onDownloadAll,
  eltRecovery,
}) => {
  const filteredJobHistory = useMemo(
    () => jobHistory.filter((job) => job.type === selectedModel),
    [jobHistory, selectedModel]
  );

  // Recovery actions only apply to the most recent batch — earlier batches
  // are immutable history. Pick the latest by ``started_at`` so the choice
  // doesn't depend on whatever order the parent merged the histories in.
  const latestBatchId = useMemo(() => {
    if (filteredJobHistory.length === 0) return null;
    return filteredJobHistory.reduce((latest, job) =>
      new Date(job.started_at).getTime() > new Date(latest.started_at).getTime()
        ? job
        : latest
    ).batch_id;
  }, [filteredJobHistory]);

  if (filteredJobHistory.length === 0) {
    return (
      <Message
        severity="info"
        text="No job history available"
        className={styles.messageMargin}
      />
    );
  }

  return (
    <DataTable value={filteredJobHistory} sortField="started_at" sortOrder={-1}>
      <Column
        field="status"
        header="Status"
        body={renderStatusCell}
        style={{ minWidth: "160px" }}
        align="left"
      />
      <Column
        field="started_at"
        header="Started"
        sortable
        body={(job: CatJobHistoryItem) => formatTimestamp(job.started_at)}
        style={{ minWidth: "160px" }}
        align="right"
      />
      <Column
        header="Duration"
        body={(job: CatJobHistoryItem) =>
          job.status === WorkStatus.DONE || job.status === WorkStatus.FAILED
            ? formatDuration(job.started_at, job.finished_at)
            : "—"
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
      <Column
        field="num_addresses"
        header="Addresses"
        body={(job: CatJobHistoryItem) =>
          job.num_addresses?.toLocaleString() ?? "—"
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
      <Column
        field="total_tiv"
        header="Total TIV"
        body={(job: CatJobHistoryItem) =>
          job.total_tiv
            ? formatExactMoneyDollars(job.total_tiv, {
                withRounding: true,
                withDollarSign: true,
              })
            : "—"
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        field="total_aal"
        header="Total AAL (Ground Up)"
        body={(job: CatJobHistoryItem) =>
          job.total_aal
            ? formatExactMoneyDollars(job.total_aal, {
                withRounding: true,
                withDollarSign: true,
              })
            : "—"
        }
        style={{ minWidth: "120px" }}
        align="right"
      />
      <Column
        header=""
        body={(job: CatJobHistoryItem) => {
          // While any recovery is kicking off, disable the rest so the
          // user can't double-fire on the same model.
          const recoveryBusy =
            eltRecovery != null &&
            (eltRecovery.disabled || eltRecovery.actionInFlightBatchId != null);
          return (
            <div className={styles.actionButtons}>
              {eltRecovery &&
                job.batch_id === latestBatchId &&
                job.status === WorkStatus.FAILED && (
                  <Button
                    icon="pi pi-forward"
                    text
                    rounded
                    severity="secondary"
                    tooltip="Resume this run from where it stalled"
                    tooltipOptions={{ position: "left" }}
                    loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                    disabled={recoveryBusy}
                    onClick={() => eltRecovery.onResume(job.batch_id)}
                  />
                )}
              {eltRecovery?.onRestart &&
                job.batch_id === latestBatchId &&
                job.status === WorkStatus.FAILED && (
                  <Button
                    icon="pi pi-replay"
                    text
                    rounded
                    severity="secondary"
                    tooltip="Restart this run: failed tasks restart their process with fresh requests to the modeling provider"
                    tooltipOptions={{ position: "left" }}
                    loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                    disabled={recoveryBusy}
                    onClick={() => {
                      eltRecovery.onRestart?.(job.batch_id);
                    }}
                  />
                )}
              {eltRecovery &&
                job.batch_id === latestBatchId &&
                job.status === WorkStatus.DONE && (
                  <Button
                    icon="pi pi-refresh"
                    text
                    rounded
                    severity="secondary"
                    tooltip="Re-run only the financial model, reusing this batch's cached modeling outputs"
                    tooltipOptions={{ position: "left" }}
                    loading={eltRecovery.actionInFlightBatchId === job.batch_id}
                    disabled={recoveryBusy}
                    onClick={() => eltRecovery.onRerunFinancials(job.batch_id)}
                  />
                )}
              {job.status === WorkStatus.DONE && (
                <Button
                  icon="pi pi-eye"
                  text
                  rounded
                  severity={
                    viewingBatchId === job.batch_id ? "info" : "secondary"
                  }
                  tooltip="View these results"
                  tooltipOptions={{ position: "left" }}
                  onClick={() => onViewJobResults(job)}
                  disabled={viewingBatchId === job.batch_id}
                />
              )}
              <Button
                icon="pi pi-download"
                text
                rounded
                severity="secondary"
                tooltip="Download all artifacts"
                tooltipOptions={{ position: "left" }}
                onClick={() => onDownloadAll(job)}
              />
            </div>
          );
        }}
        className={styles.actionsColumn}
        align="center"
      />
    </DataTable>
  );
};
